import api from "./api";
import { generateErrorMessage } from "../utils/message";


/*Vendors*/
export const registerVendor = async (vendorData) => {
  try {
    const response = await api.post("/vendors", vendorData);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("register", "vendor");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};


export const fetchAllVendorsFromAPI = async () => {
  try {
    const response = await api.get("/vendors/all/page");

    return response.data.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data", "vendor");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const fetchDistinctVendorsFromAPI = async () => {
  try {
    const response = await api.get("/vendors/all/distinct");

    return response.data.data;
  } catch (error) {
    const fallback = generateErrorMessage("get distinct vendors", "vendor");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const fetchVendorsFromAPI = async (page = 1, perPage = 10, search = "", columnFilters = {}, sort = {}) => {
  try {
    // Build params object with column-specific filters
    const params = {
      page,
      per_page: perPage
    };

    // Add search parameter if provided (for backward compatibility)
    if (search) {
      params.search = search;
    }

    // Add column-specific parameters
    if (columnFilters.vendor_code) params.vendor_code = columnFilters.vendor_code;
    if (columnFilters.vendor_name) params.vendor_name = columnFilters.vendor_name;
    if (columnFilters.destination) params.destination = columnFilters.destination;
    if (columnFilters.pic) params.pic = columnFilters.pic;
    if (columnFilters.email) params.email = columnFilters.email;

    // Server-side sort
    if (sort && sort.sortBy) {
      params.sort_by = sort.sortBy;
      params.sort_dir = sort.sortDir === 'desc' ? 'desc' : 'asc';
    }

    const response = await api.get("/vendors", {
      params: params,
    });

    return response.data.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data", "vendor");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};


/* 📌 Fetch every vendor matching the current filters (for Export) */
// The list endpoint is server-side paginated and caps per_page at 1000, so a
// single request can never return the whole result set — this walks the pages.
// `/vendors/all/page` is not usable here because it ignores search and filters.
export const fetchAllVendorsForExport = async (
  { search = "", columnFilters = {}, sort = {} } = {},
  onProgress,
) => {
  const PER_PAGE = 1000;
  const collected = [];
  let page = 1;

  for (;;) {
    const response = await fetchVendorsFromAPI(page, PER_PAGE, search, columnFilters, sort);
    const payload = response?.vendors || {};
    const rows = Array.isArray(payload.data) ? payload.data : [];

    collected.push(...rows);

    const reportedTotal = Number(payload.total);
    const hasTotal = Number.isFinite(reportedTotal) && reportedTotal > 0;

    if (onProgress) {
      onProgress(collected.length, hasTotal ? reportedTotal : collected.length);
    }

    const reportedLastPage = Number(payload.last_page);
    const hasLastPage = Number.isFinite(reportedLastPage) && reportedLastPage > 0;

    // A short page is the reliable end-of-data signal, so the export still
    // completes if the payload ever omits `total` or `last_page`. Stopping on a
    // missing field instead would silently truncate the file — the exact bug
    // this function exists to fix.
    if (rows.length === 0) break;
    if (hasLastPage && page >= reportedLastPage) break;
    if (hasTotal && collected.length >= reportedTotal) break;
    if (rows.length < PER_PAGE) break;

    page += 1;
  }

  return collected;
};

export const deleteVendorsFromAPI = async (vendorId) => {
  try {
    const response = await api.delete(`/vendors/${vendorId}`, {
      params: { vendor_id: vendorId },
    });
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("delete data", "vendor");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};


export const fetchVendorsById = async (vendorId) => {
  try {    
    const response = await api.get(`/vendors/${vendorId}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data vendor", "vendor");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const updateVendor = async (id, vendorData) => {
  try {
    const response = await api.put(`/vendors/${id}`, vendorData);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("update", "vendor");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const uploadVendorFileToAPI = async (formData, onUploadProgress) => {
  try {
    const response = await api.post("/vendors/submit-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      // Reports how much of the FILE has reached the server (0-100). Server-side
      // row inserting happens after this hits 100 and is not tracked here.
      onUploadProgress: onUploadProgress
        ? (event) => {
            if (event.total) {
              onUploadProgress(Math.round((event.loaded / event.total) * 100));
            }
          }
        : undefined,
    });
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("upload vendor file", "vendor");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Sync Vendors from External API */
export const syncVendorsFromAPI = async (activeOnly = false, keepExisting = false) => {
  try {
    const params = new URLSearchParams({
      active_only: activeOnly.toString(),
      keep_existing: keepExisting.toString()
    });
    
    const response = await api.post(`/sync/vendors?${params}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("sync", "vendor");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Test Sync Connection */
export const testSyncConnection = async () => {
  try {
    const response = await api.get("/sync/test-connection");
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("test connection", "sync");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};


